/**
 * Pharmacy Benefit Analysis (super_admin only)
 *
 * Aggregate analyses over the legacy mdexam medical_history collection.
 * Ported from the Streamlit Pharmacy Benefit page.
 */

import { useEffect, useState, useCallback } from 'react'
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { pharmacyBenefitAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui'
import { Loader2, AlertCircle } from 'lucide-react'

const PERIOD_OPTIONS = [
  { label: 'Last 6 Months', value: 6 },
  { label: 'Last 12 Months', value: 12 },
  { label: 'Last 18 Months', value: 18 },
  { label: 'Last 24 Months', value: 24 },
]

const PIE_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
]

const SCHEDULE_LABELS = { 2: 'Schedule II', 3: 'Schedule III', 4: 'Schedule IV', 5: 'Schedule V' }

function fmt(n, digits = 0) {
  if (n == null || Number.isNaN(n)) return '–'
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: digits })
}

function Metric({ label, value }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  )
}

function Spinner({ label }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  )
}

function ErrorBox({ message }) {
  return (
    <div className="flex items-center gap-2 text-destructive py-4">
      <AlertCircle className="h-4 w-4" /> {message}
    </div>
  )
}

function useEndpoint(fn, monthsBack) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fn({ monthsBack })
      if (res.data?.success) setData(res.data.data)
      else throw new Error(res.data?.error?.message || 'Request failed')
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [fn, monthsBack])

  useEffect(() => { load() }, [load])
  return { data, loading, error, reload: load }
}

// ─── Tabs ──────────────────────────────────────────────────

function DrugsTab({ monthsBack }) {
  const { data, loading, error } = useEndpoint(pharmacyBenefitAPI.getDrugs, monthsBack)
  if (loading) return <Spinner label="Loading drug analysis…" />
  if (error) return <ErrorBox message={error} />
  if (!data) return null

  const brandPie = data.brand.map(b => ({
    name: b.is_brand ? 'Brand Name' : 'Generic',
    value: b.count,
  }))

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Top 10 Drug Classifications</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie data={data.classifications} dataKey="count" nameKey="name" outerRadius={110} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                  {data.classifications.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Brand vs Generic</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie data={brandPie} dataKey="value" nameKey="name" outerRadius={110} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                  <Cell fill="#3b82f6" />
                  <Cell fill="#f97316" />
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Top 15 Most Prescribed Drugs</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={[...data.top_drugs].reverse()} layout="vertical" margin={{ left: 200 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Drug Classification Summary</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4">Classification</th>
                <th className="py-2 pr-4 text-right">Patients</th>
                <th className="py-2 pr-4 text-right">Drugs</th>
                <th className="py-2 pr-4 text-right">Brand</th>
                <th className="py-2 pr-4 text-right">Avg Qty</th>
                <th className="py-2 pr-4 text-right">Avg Days Supply</th>
              </tr>
            </thead>
            <tbody>
              {data.class_summary.map(row => (
                <tr key={row.classification} className="border-b">
                  <td className="py-2 pr-4">{row.classification}</td>
                  <td className="py-2 pr-4 text-right">{fmt(row.unique_patients)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(row.unique_drugs)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(row.brand_count)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(row.avg_quantity, 1)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(row.avg_days_supply, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

function PayersTab({ monthsBack }) {
  const { data, loading, error } = useEndpoint(pharmacyBenefitAPI.getPayers, monthsBack)
  if (loading) return <Spinner label="Loading payer analysis…" />
  if (error) return <ErrorBox message={error} />
  if (!data) return null

  // Stacked bar: one row per top-5 payer, columns are the top classifications
  // (server caps to 5 + "Other") so the chart stays readable.
  const payers = data.top_payers || data.payer_counts.slice(0, 5).map(p => p.payer)
  const classes = data.top_classes || Array.from(new Set(data.payer_class.map(r => r.classification)))
  const stacked = payers.map(payer => {
    const counts = Object.fromEntries(classes.map(c => [c, 0]))
    for (const r of data.payer_class) {
      if (r.payer === payer && r.classification in counts) {
        counts[r.classification] += r.count
      }
    }
    return { payer, ...counts }
  })

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Prescription Coverage by Payer</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={360}>
              <PieChart>
                <Pie data={data.payer_counts} dataKey="count" nameKey="payer" outerRadius={120} label={({ payer, percent }) => `${payer} (${(percent * 100).toFixed(0)}%)`}>
                  {data.payer_counts.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Drug Classifications by Top 5 Payers</CardTitle>
            <CardDescription>Top {classes.length - 1} classes; the rest grouped as Other</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={stacked} layout="vertical" margin={{ left: 40, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => fmt(v)} />
                <YAxis type="category" dataKey="payer" width={140} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {classes.map((cls, i) => (
                  <Bar key={cls} dataKey={cls} stackId="a" fill={cls === 'Other' ? '#94a3b8' : PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Payer Performance Metrics</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4">Payer</th>
                <th className="py-2 pr-4 text-right">Patients</th>
                <th className="py-2 pr-4 text-right">Drugs</th>
                <th className="py-2 pr-4 text-right">Avg Qty</th>
                <th className="py-2 pr-4 text-right">Avg Days Supply</th>
                <th className="py-2 pr-4 text-right">Brand %</th>
              </tr>
            </thead>
            <tbody>
              {data.payer_metrics.map(r => (
                <tr key={r.payer} className="border-b">
                  <td className="py-2 pr-4">{r.payer}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.unique_patients)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.unique_drugs)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.avg_quantity, 1)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.avg_days_supply, 1)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.brand_pct, 1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

function FillPatternsTab({ monthsBack }) {
  const { data, loading, error } = useEndpoint(pharmacyBenefitAPI.getFillPatterns, monthsBack)
  if (loading) return <Spinner label="Loading fill patterns…" />
  if (error) return <ErrorBox message={error} />
  if (!data) return null

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Monthly Prescription Fills Trend</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={data.monthly_fills}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Days Supply Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.days_supply}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="days" />
                <YAxis />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Refills Distribution</CardTitle></CardHeader>
          <CardContent>
            {data.refills.length ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.refills}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="refills" />
                  <YAxis />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="count" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-muted-foreground text-sm">Refills data not available</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function PatientsTab({ monthsBack }) {
  const { data, loading, error } = useEndpoint(pharmacyBenefitAPI.getPatients, monthsBack)
  if (loading) return <Spinner label="Loading patient analysis…" />
  if (error) return <ErrorBox message={error} />
  if (!data) return null

  const stats = data.polypharmacy_stats

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Medications per Patient</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.distribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="med_count" />
                <YAxis />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="patients" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Polypharmacy</CardTitle>
            <CardDescription>Patients with 5+ medications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between"><span className="text-muted-foreground">Total Patients</span><span className="font-medium">{fmt(stats.total_patients)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Polypharmacy Patients</span><span className="font-medium">{fmt(stats.polypharmacy_patients)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Polypharmacy Rate</span><span className="font-medium">{fmt(stats.polypharmacy_rate, 1)}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Avg Meds (All)</span><span className="font-medium">{fmt(stats.avg_meds_all, 1)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Avg Meds (Polypharmacy)</span><span className="font-medium">{fmt(stats.avg_meds_poly, 1)}</span></div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Top 10 Most Complex Patients</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4">Customer ID</th>
                <th className="py-2 pr-4 text-right">Drug Classes</th>
                <th className="py-2 pr-4 text-right">Brand Meds</th>
                <th className="py-2 pr-4 text-right">Payers</th>
                <th className="py-2 pr-4 text-right">Unique Drugs</th>
                <th className="py-2 pr-4 text-right">Complexity</th>
              </tr>
            </thead>
            <tbody>
              {data.top_complex.map(r => (
                <tr key={r.customer_id} className="border-b">
                  <td className="py-2 pr-4 font-mono text-xs">{r.customer_id}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.drug_classes)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.brand_count)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.payers)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.unique_drugs)}</td>
                  <td className="py-2 pr-4 text-right">{fmt(r.complexity_score)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

function TrendsTab({ monthsBack }) {
  const { data, loading, error } = useEndpoint(pharmacyBenefitAPI.getTrends, monthsBack)
  if (loading) return <Spinner label="Loading trends…" />
  if (error) return <ErrorBox message={error} />
  if (!data) return null

  const ins = data.insights
  const schedulesPie = data.schedules.map(s => ({
    name: SCHEDULE_LABELS[s.schedule] || `Schedule ${s.schedule}`,
    value: s.count,
  }))

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Time Since Last Fill</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.recency}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Controlled Substances by Schedule</CardTitle></CardHeader>
          <CardContent>
            {schedulesPie.length ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={schedulesPie} dataKey="value" nameKey="name" outerRadius={110} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                    {schedulesPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-muted-foreground text-sm">No controlled substances in current dataset</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Key Insights</CardTitle></CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li><strong>Most Common Drug Class</strong>: {ins.top_class_name || '–'} ({fmt(ins.top_class_count)} prescriptions)</li>
            <li><strong>Brand vs Generic</strong>: {fmt(ins.brand_pct, 1)}% brand name, {fmt(100 - ins.brand_pct, 1)}% generic</li>
            <li><strong>Primary Payer</strong>: {ins.top_payer_name || '–'} ({fmt(ins.top_payer_count)} prescriptions)</li>
            <li><strong>Polypharmacy Rate</strong>: {fmt(ins.polypharmacy_rate, 1)}% of patients have 5+ medications</li>
            <li><strong>Average Days Supply</strong>: {fmt(ins.avg_days_supply, 0)} days per prescription</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────

export default function PharmacyBenefitPage() {
  const [monthsBack, setMonthsBack] = useState(12)
  const { currentClinicId } = useAuthStore()
  const { data: summary, loading, error } = useEndpoint(pharmacyBenefitAPI.getSummary, monthsBack)

  if (currentClinicId !== 'mdexam_legacy') {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>🏥 Pharmacy Benefit Analysis</CardTitle>
            <CardDescription>Only available for the mdexam (legacy) clinic.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Switch to the mdexam (legacy) clinic from the sidebar to view this report.
              Other clinics don't ingest external pharmacy benefit / dispense history.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">🏥 Pharmacy Benefit Analysis</h1>
          <p className="text-muted-foreground text-sm">
            Medication history and pharmacy benefit utilization (mdexam legacy)
          </p>
        </div>
        <Select value={String(monthsBack)} onValueChange={(v) => setMonthsBack(Number(v))}>
          <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Metric label="Total Patients" value={loading ? '…' : fmt(summary?.total_customers)} />
        <Metric label="Total Medications" value={loading ? '…' : fmt(summary?.total_medications)} />
        <Metric label="Unique Drugs" value={loading ? '…' : fmt(summary?.unique_drugs)} />
        <Metric label="Avg Meds/Patient" value={loading ? '…' : fmt(summary?.avg_meds_per_patient, 1)} />
        <Metric label="Recent Fills (30d)" value={loading ? '…' : fmt(summary?.recent_fills)} />
      </div>

      <Tabs defaultValue="drugs">
        <TabsList>
          <TabsTrigger value="drugs">💊 Drug Analysis</TabsTrigger>
          <TabsTrigger value="payers">🏢 Payer Analysis</TabsTrigger>
          <TabsTrigger value="fill">📅 Fill Patterns</TabsTrigger>
          <TabsTrigger value="patients">👥 Patient Analysis</TabsTrigger>
          <TabsTrigger value="trends">📈 Trends</TabsTrigger>
        </TabsList>
        <TabsContent value="drugs"><DrugsTab monthsBack={monthsBack} /></TabsContent>
        <TabsContent value="payers"><PayersTab monthsBack={monthsBack} /></TabsContent>
        <TabsContent value="fill"><FillPatternsTab monthsBack={monthsBack} /></TabsContent>
        <TabsContent value="patients"><PatientsTab monthsBack={monthsBack} /></TabsContent>
        <TabsContent value="trends"><TrendsTab monthsBack={monthsBack} /></TabsContent>
      </Tabs>
    </div>
  )
}
